import React, { useState } from "react";
import { BASE_URL } from "../../../services/configUrls";
import api from "../../../services/api";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton,
  InputAdornment, CircularProgress, Divider, Avatar,
  MenuItem, Select, FormControl, FormHelperText,
  Stepper, Step, StepLabel,
} from "@mui/material";
import {
  Close as CloseIcon, Search as SearchIcon,
  Business as BusinessIcon, CheckCircle as CheckCircleIcon,
  PersonAdd as PersonAddIcon, Email as EmailIcon,
  VerifiedUser as VerifiedUserIcon,
  Lock as LockIcon,        // ✅ new
  Shield as ShieldIcon,   // ✅ new
} from "@mui/icons-material";

const BLUE = {
  main: "#2196F3", light: "#1976D2", dark: "#1565C0",
  gradient: "linear-gradient(90deg, #1976D2 0%, #42A5F5 100%)",
  gradientDark: "linear-gradient(90deg, #0D47A1 0%, #1976D2 100%)",
  solight: "#EEF7FE", white: "#FFFFFF",
};

// ✅ Step -1 is the new self-verification gate
const STEP_SELF_OTP  = 0;   // Verify current SPOC identity
const STEP_SEARCH    = 1;   // Search new SPOC email
const STEP_REGISTER  = 2;   // Register new user (user_not_found path)
const STEP_OTP       = 3;   // Verify new SPOC email OTP
const STEP_DONE      = 4;   // Confirm & assign

const AssignSpocDialog = ({ open, onClose, member, isDarkMode, onAssignSuccess }) => {

  // ── Self OTP (identity gate) ──
  const [selfOtp, setSelfOtp]               = useState("");
  const [selfOtpError, setSelfOtpError]     = useState("");
  const [selfOtpSent, setSelfOtpSent]       = useState(false);
  const [maskedEmail, setMaskedEmail]       = useState("");
  const [selfResendLoading, setSelfResendLoading] = useState(false);

  // ── Search ──
  const [email, setEmail]               = useState("");
  const [emailError, setEmailError]     = useState("");
  const [loading, setLoading]           = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searched, setSearched]         = useState(false);
  const [searchStatus, setSearchStatus] = useState("");
  const [designations, setDesignations] = useState([]);

  // ── Register ──
  const [regForm, setRegForm] = useState({
    firstName: "", middleName: "", lastName: "",
    mobile: "", desigId: "",
  });
  const [regErrors, setRegErrors] = useState({});

  // ── OTP (new SPOC email verify) ──
  const [otp, setOtp]                     = useState("");
  const [otpError, setOtpError]           = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  const [step, setStep] = useState(STEP_SELF_OTP);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px", fontSize: "0.85rem",
      backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "#fafafa",
    },
  };
  const labelSx = {
    fontSize: "0.82rem", fontWeight: 500, mb: 0.5,
    color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.65)",
  };
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validateEmail = (v) => {
    if (!v?.trim()) return "Email address is required";
    if (!emailRegex.test(v.trim())) return "Please enter a valid email address";
    return "";
  };

  // ─── Self OTP — Send ──────────────────────────────────────────────────────
  const handleSendSelfOtp = async () => {
    setLoading(true);
    setSelfOtpError("");
    try {
      const res = await api.post(`${BASE_URL}/staff/send_self_otp`);
      setMaskedEmail(res.data.masked_email || "your registered email");
      setSelfOtpSent(true);
    } catch (err) {
      setSelfOtpError(err.response?.data?.detail || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendSelfOtp = async () => {
    setSelfResendLoading(true);
    setSelfOtp("");
    setSelfOtpError("");
    try {
      const res = await api.post(`${BASE_URL}/staff/send_self_otp`);
      setMaskedEmail(res.data.masked_email || "your registered email");
    } catch (err) {
      setSelfOtpError(err.response?.data?.detail || "Failed to resend OTP.");
    } finally {
      setSelfResendLoading(false);
    }
  };

  // ─── Self OTP — Verify ────────────────────────────────────────────────────
  const handleVerifySelfOtp = async () => {
    if (selfOtp.trim().length < 6) {
      setSelfOtpError("Please enter the 6-digit OTP");
      return;
    }
    setSelfOtpError("");
    setLoading(true);
    try {
      await api.post(`${BASE_URL}/staff/verify_self_otp`, { otp: selfOtp.trim() });
      setStep(STEP_SEARCH);  // ✅ Identity confirmed — proceed to email search
    } catch (err) {
      setSelfOtpError(err.response?.data?.detail || "Incorrect OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Search ───────────────────────────────────────────────────────────────
  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    if (emailError) setEmailError(validateEmail(val));
    if (searched) { setSearchResult(null); setSearched(false); setSearchStatus(""); }
  };

  const handleSearch = async () => {
    const err = validateEmail(email);
    if (err) { setEmailError(err); return; }
    setEmailError("");
    setLoading(true);
    setSearched(false);
    setSearchResult(null);
    setSearchStatus("");
    try {
      const response = await api(`${BASE_URL}/staff/check_email?email=${email.trim()}`);
      const data = response.data;
      if (data.designitions) setDesignations(data.designitions);
      if (data.status === "already spoc")                                setSearchStatus("already_spoc");
      else if (data.status === "user not found")                         setSearchStatus("user_not_found");
      else if (data.status === "present in educator table" ||
               data.status === "present in academy table") { setSearchResult(data); setSearchStatus("found"); }
      else                                                               setSearchStatus("user_not_found");
      setSearched(true);
    } catch {
      setEmailError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Send OTP to new SPOC email (found user) ─────────────────────────────
  const handleSendOtpDirect = async () => {
    setLoading(true);
    try {
      await api.post(`${BASE_URL}/staff/send_otp`, { email: email.trim() });
      setStep(STEP_OTP);
    } catch (err) {
      setEmailError(err.response?.data?.detail || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Register form (new user path) ───────────────────────────────────────
  const validateRegForm = () => {
    const e = {};
    if (!regForm.firstName.trim())  e.firstName = "First name is required";
    if (!regForm.lastName.trim())   e.lastName  = "Last name is required";
    if (!regForm.desigId)           e.desigId   = "Designation is required";
    if (!regForm.mobile.trim())     e.mobile    = "Mobile number is required";
    else if (!/^\d{10}$/.test(regForm.mobile.trim())) e.mobile = "Enter a valid 10-digit mobile number";
    return e;
  };

  const handleRegChange = (field) => (e) => {
    setRegForm((p) => ({ ...p, [field]: e.target.value }));
    if (regErrors[field]) setRegErrors((p) => ({ ...p, [field]: "" }));
  };

  const handleSendOtp = async () => {
  const errors = validateRegForm();
  if (Object.keys(errors).length > 0) { setRegErrors(errors); return; }
  setLoading(true);
  try {
    await api.post(`${BASE_URL}/staff/send_otp`, {
      email: email.trim(),
      mobile: regForm.mobile.trim(),   // ✅ send mobile for duplicate check
    });
    setStep(STEP_OTP);
  } catch (err) {
    const detail = err.response?.data?.detail || "";
    // ✅ Surface mobile-specific error on the field itself
    if (err.response?.status === 409 || detail.toLowerCase().includes("mobile")) {
      setRegErrors((p) => ({ ...p, mobile: detail || "Mobile number already exists" }));
    } else {
      setRegErrors({ _form: detail || "Failed to send OTP. Please try again." });
    }
  } finally {
    setLoading(false);
  }
};

  // ─── Verify new SPOC OTP ─────────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    if (otp.trim().length < 6) { setOtpError("Please enter the 6-digit OTP"); return; }
    setOtpError("");
    setLoading(true);
    try {
      await api.post(`${BASE_URL}/staff/verify_otp`, { email: email.trim(), otp: otp.trim() });
      setStep(STEP_DONE);
    } catch (err) {
      setOtpError(err.response?.data?.detail || "Incorrect OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setOtp(""); setOtpError("");
    try {
      await api.post(`${BASE_URL}/staff/send_otp`, { email: email.trim() });
    } catch (err) {
      setOtpError(err.response?.data?.detail || "Failed to resend OTP.");
    } finally {
      setResendLoading(false);
    }
  };

  // ─── Assign SPOC ─────────────────────────────────────────────────────────
  const handleAssignSpoc = async () => {
    setLoading(true);
    try {
      await api.post(`${BASE_URL}/staff/assign_spoc`, {
        spoc_id: member?.id,
        email: email.trim(),
        ...(searchStatus === "user_not_found" && {
          first_name:  regForm.firstName  || undefined,
          middle_name: regForm.middleName || undefined,
          last_name:   regForm.lastName   || undefined,
          desig_id:    regForm.desigId    || undefined,
          mobile:      regForm.mobile     || undefined,
        }),
      });
      const displayName = searchStatus === "user_not_found"
        ? `${regForm.firstName} ${regForm.lastName}`
        : `${searchResult?.role || "User"} — ${email}`;
      onAssignSuccess?.(`SPOC assigned to ${displayName} successfully`);
      handleClose();
      localStorage.clear();
      window.location.href = "/login";
    } catch (err) {
      setOtpError(err.response?.data?.detail || "Failed to assign SPOC. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Reset ────────────────────────────────────────────────────────────────
  const handleClose = () => {
    setSelfOtp(""); setSelfOtpError(""); setSelfOtpSent(false); setMaskedEmail("");
    setEmail(""); setEmailError(""); setLoading(false);
    setSearchResult(null); setSearched(false); setSearchStatus("");
    setDesignations([]);
    setRegForm({ firstName: "", middleName: "", lastName: "", desigId: "", mobile: "" });
    setRegErrors({});
    setOtp(""); setOtpError(""); setResendLoading(false);
    setStep(STEP_SELF_OTP);  // ← always reset to gate
    onClose();
  };

  const showStepper = searchStatus === "user_not_found" && step >= STEP_REGISTER;
  const handleOtpBack = () => {
    if (searchStatus === "user_not_found") setStep(STEP_REGISTER);
    else setStep(STEP_SEARCH);
  };

  return (
    <Dialog
      open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: isDarkMode ? "#1e2a3a" : "#ffffff",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ background: isDarkMode ? BLUE.gradientDark : BLUE.gradient, px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <BusinessIcon sx={{ color: BLUE.white, fontSize: "1.3rem" }} />
            <Typography sx={{ color: BLUE.white, fontWeight: 700, fontSize: "1rem" }}>
              {step === STEP_SELF_OTP ? "Identity Verification" : "Assign SPOC"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ color: BLUE.white, "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 3 }}>

        {/* Stepper — only for new user path */}
        {showStepper && (
          <Stepper activeStep={step - STEP_REGISTER} alternativeLabel sx={{ mb: 3, "& .MuiStepLabel-label": { fontSize: "0.72rem" } }}>
            {["Details", "Verify OTP", "Assign"].map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        )}

        {/* ══ STEP SELF_OTP — Identity Gate ══ */}
        {step === STEP_SELF_OTP && (
          <>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box sx={{
                width: 60, height: 60, borderRadius: "50%",
                backgroundColor: isDarkMode ? "rgba(33,150,243,0.15)" : BLUE.solight,
                display: "flex", alignItems: "center", justifyContent: "center",
                mx: "auto", mb: 1.5
              }}>
                <ShieldIcon sx={{ fontSize: "1.8rem", color: BLUE.main }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: isDarkMode ? "white" : "rgba(0,0,0,0.85)", mb: 0.5 }}>
                Verify Your Identity
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: isDarkMode ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)", lineHeight: 1.5 }}>
                {!selfOtpSent
                  ? "To transfer your SPOC credentials to another person, need to verify your identity first. Click below to receive an OTP on your registered email."
                  : <>We've sent a 6-digit OTP to <strong style={{ color: BLUE.main }}>{maskedEmail}</strong></>
                }
              </Typography>
            </Box>

            {selfOtpSent && (
              <>
                <Typography sx={labelSx}>Enter OTP *</Typography>
                <TextField
                  fullWidth size="small" placeholder="Enter 6-digit OTP"
                  value={selfOtp}
                  onChange={(e) => { setSelfOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); if (selfOtpError) setSelfOtpError(""); }}
                  error={Boolean(selfOtpError)} helperText={selfOtpError}
                  inputProps={{ maxLength: 6, style: { letterSpacing: "0.3em", fontSize: "1.1rem", textAlign: "center" } }}
                  sx={{ ...inputSx, mb: 2 }}
                  FormHelperTextProps={{ sx: { fontSize: "0.75rem", textAlign: "center" } }}
                />
                <Box sx={{ textAlign: "center" }}>
                  <Typography component="span" sx={{ fontSize: "0.78rem", color: isDarkMode ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)" }}>
                    Didn't receive it?{" "}
                  </Typography>
                  <Typography component="span"
                    onClick={selfResendLoading ? undefined : handleResendSelfOtp}
                    sx={{ fontSize: "0.78rem", fontWeight: 600, color: BLUE.main, cursor: selfResendLoading ? "default" : "pointer", opacity: selfResendLoading ? 0.5 : 1, "&:hover": { textDecoration: "underline" } }}
                  >
                    {selfResendLoading ? "Sending..." : "Resend OTP"}
                  </Typography>
                </Box>
              </>
            )}

            {selfOtpError && !selfOtpSent && (
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: "8px", backgroundColor: isDarkMode ? "rgba(255,82,82,0.1)" : "#fff5f5", border: "1px solid rgba(244,67,54,0.2)" }}>
                <Typography sx={{ fontSize: "0.8rem", color: "#f44336" }}>{selfOtpError}</Typography>
              </Box>
            )}
          </>
        )}

        {/* ══ STEP SEARCH ══ */}
        {step === STEP_SEARCH && (
          <>
            <Typography variant="body2" sx={{ color: isDarkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)", fontSize: "0.82rem", mb: 2.5, lineHeight: 1.5 }}>
              Enter the email address of the user you want to assign as SPOC and click <strong>Search</strong>.
            </Typography>

            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <TextField
                fullWidth size="small" placeholder="Enter email address"
                value={email} onChange={handleEmailChange}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                error={Boolean(emailError)} helperText={emailError}
                type="email" autoComplete="off" sx={inputSx}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: emailError ? "#f44336" : isDarkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)" }} />
                  </InputAdornment>
                )}}
                FormHelperTextProps={{ sx: { fontSize: "0.75rem", mt: 0.5, ml: 0.5 } }}
              />
              <Button
                variant="contained" onClick={handleSearch} disabled={loading}
                sx={{ borderRadius: "10px", minWidth: "90px", height: "40px", flexShrink: 0, background: isDarkMode ? BLUE.gradientDark : BLUE.gradient, textTransform: "none", fontWeight: 600, fontSize: "0.85rem", boxShadow: "0 3px 10px rgba(33,150,243,0.3)", "&:hover": { background: BLUE.gradientDark } }}
              >
                {loading ? <CircularProgress size={16} sx={{ color: "white" }} /> : "Submit"}
              </Button>
            </Box>

            {/* Found */}
            {searched && searchStatus === "found" && searchResult && (
              <Box sx={{ mt: 2.5, p: 2, borderRadius: "12px", border: `1.5px solid ${isDarkMode ? "rgba(33,150,243,0.3)" : "rgba(33,150,243,0.25)"}`, backgroundColor: isDarkMode ? "rgba(33,150,243,0.07)" : BLUE.solight }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <Avatar sx={{ bgcolor: BLUE.main, color: "white", width: 38, height: 38, fontSize: "0.95rem", fontWeight: 700 }}>
                    {email.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, overflow: "hidden" }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.88rem", color: isDarkMode ? "white" : "rgba(0,0,0,0.85)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {email}
                    </Typography>
                    <Typography sx={{ fontSize: "0.77rem", color: isDarkMode ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)" }}>
                      Current Role: <strong>{searchResult.role}</strong>
                    </Typography>
                  </Box>
                  <CheckCircleIcon sx={{ color: "#4caf50", fontSize: "1.3rem", flexShrink: 0 }} />
                </Box>
                <Typography sx={{ fontSize: "0.78rem", color: isDarkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)" }}>
                  An OTP will be sent to this email to verify before assigning as SPOC.
                </Typography>
              </Box>
            )}

            {/* Already SPOC */}
            {searched && searchStatus === "already_spoc" && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: "10px", backgroundColor: isDarkMode ? "rgba(255,152,0,0.08)" : "#fff8e1", border: "1px solid rgba(255,152,0,0.3)", textAlign: "center" }}>
                <Typography sx={{ fontSize: "0.82rem", color: "#e65100", fontWeight: 500 }}>⚠️ This user is already assigned as a SPOC.</Typography>
              </Box>
            )}

            {/* User not found */}
            {searched && searchStatus === "user_not_found" && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: "10px", backgroundColor: isDarkMode ? "rgba(255,82,82,0.08)" : "#fff5f5", border: "1px solid rgba(244,67,54,0.2)" }}>
                <Typography sx={{ fontSize: "0.82rem", color: "#f44336", fontWeight: 500, mb: 0.75 }}>❌ No user found with this email.</Typography>
                <Typography sx={{ fontSize: "0.78rem", color: isDarkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", mb: 1.5 }}>
                  Fill in the details below to create a new SPOC account.
                </Typography>
                <Button
                  size="small" variant="outlined"
                  startIcon={<PersonAddIcon sx={{ fontSize: "14px !important" }} />}
                  onClick={() => setStep(STEP_REGISTER)}
                  sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.8rem", borderColor: BLUE.main, color: BLUE.main, "&:hover": { backgroundColor: "rgba(33,150,243,0.06)" } }}
                >
                  Create New SPOC Account
                </Button>
              </Box>
            )}
          </>
        )}

        {/* ══ STEP REGISTER ══ */}
        {step === STEP_REGISTER && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <EmailIcon sx={{ fontSize: "1rem", color: BLUE.main }} />
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: isDarkMode ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)" }}>
                Creating account for: <span style={{ color: BLUE.main }}>{email}</span>
              </Typography>
            </Box>

            {regErrors._form && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: "8px", backgroundColor: isDarkMode ? "rgba(255,82,82,0.1)" : "#fff5f5", border: "1px solid rgba(244,67,54,0.2)" }}>
                <Typography sx={{ fontSize: "0.8rem", color: "#f44336" }}>{regErrors._form}</Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={labelSx}>First Name *</Typography>
                <TextField fullWidth size="small" placeholder="First name"
                  value={regForm.firstName} onChange={handleRegChange("firstName")}
                  error={Boolean(regErrors.firstName)} helperText={regErrors.firstName}
                  sx={inputSx} FormHelperTextProps={{ sx: { fontSize: "0.72rem", ml: 0.5 } }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={labelSx}>Middle Name</Typography>
                <TextField fullWidth size="small" placeholder="Middle name"
                  value={regForm.middleName} onChange={handleRegChange("middleName")} sx={inputSx}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={labelSx}>Last Name *</Typography>
              <TextField fullWidth size="small" placeholder="Last name"
                value={regForm.lastName} onChange={handleRegChange("lastName")}
                error={Boolean(regErrors.lastName)} helperText={regErrors.lastName}
                sx={inputSx} FormHelperTextProps={{ sx: { fontSize: "0.72rem", ml: 0.5 } }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={labelSx}>Designation *</Typography>
              <FormControl fullWidth size="small" error={Boolean(regErrors.desigId)}>
                <Select value={regForm.desigId} onChange={handleRegChange("desigId")} displayEmpty
                  sx={{ borderRadius: "10px", fontSize: "0.85rem", backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "#fafafa" }}
                >
                  <MenuItem value="" disabled>
                    <em style={{ color: "rgba(0,0,0,0.38)", fontStyle: "normal" }}>Select Designation</em>
                  </MenuItem>
                  {designations.map((d) => (
                    <MenuItem key={d.design_id} value={d.design_id}>{d.title}</MenuItem>
                  ))}
                </Select>
                {regErrors.desigId && <FormHelperText sx={{ fontSize: "0.72rem" }}>{regErrors.desigId}</FormHelperText>}
              </FormControl>
            </Box>

            <Box>
              <Typography sx={labelSx}>Mobile Number *</Typography>
              <TextField fullWidth size="small" placeholder="10-digit mobile number"
                value={regForm.mobile} onChange={handleRegChange("mobile")}
                error={Boolean(regErrors.mobile)} helperText={regErrors.mobile}
                inputProps={{ maxLength: 10 }} sx={inputSx}
                FormHelperTextProps={{ sx: { fontSize: "0.72rem", ml: 0.5 } }}
              />
            </Box>
          </>
        )}

        {/* ══ STEP OTP (new SPOC email verify) ══ */}
        {step === STEP_OTP && (
          <>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: isDarkMode ? "rgba(33,150,243,0.15)" : BLUE.solight, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                <EmailIcon sx={{ fontSize: "1.6rem", color: BLUE.main }} />
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: isDarkMode ? "white" : "rgba(0,0,0,0.85)", mb: 0.5 }}>Check your email</Typography>
              <Typography sx={{ fontSize: "0.8rem", color: isDarkMode ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)" }}>We've sent a 6-digit OTP to</Typography>
              <Typography sx={{ fontSize: "0.82rem", color: BLUE.main, fontWeight: 600 }}>{email}</Typography>
            </Box>

            <Typography sx={labelSx}>Enter OTP *</Typography>
            <TextField
              fullWidth size="small" placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); if (otpError) setOtpError(""); }}
              error={Boolean(otpError)} helperText={otpError}
              inputProps={{ maxLength: 6, style: { letterSpacing: "0.3em", fontSize: "1.1rem", textAlign: "center" } }}
              sx={{ ...inputSx, mb: 2 }}
              FormHelperTextProps={{ sx: { fontSize: "0.75rem", textAlign: "center" } }}
            />
            <Box sx={{ textAlign: "center" }}>
              <Typography component="span" sx={{ fontSize: "0.78rem", color: isDarkMode ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)" }}>
                Didn't receive the OTP?{" "}
              </Typography>
              <Typography component="span" onClick={resendLoading ? undefined : handleResendOtp}
                sx={{ fontSize: "0.78rem", fontWeight: 600, color: BLUE.main, cursor: resendLoading ? "default" : "pointer", opacity: resendLoading ? 0.5 : 1, "&:hover": { textDecoration: "underline" } }}
              >
                {resendLoading ? "Sending..." : "Resend OTP"}
              </Typography>
            </Box>
          </>
        )}

        {/* ══ STEP DONE ══ */}
        {step === STEP_DONE && (
          <Box sx={{ textAlign: "center", py: 1 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: "50%", backgroundColor: "rgba(76,175,80,0.1)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
              <VerifiedUserIcon sx={{ fontSize: "1.8rem", color: "#4caf50" }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: isDarkMode ? "white" : "rgba(0,0,0,0.87)", mb: 0.75 }}>Email Verified!</Typography>
            <Typography sx={{ fontSize: "0.82rem", color: isDarkMode ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)", mb: 0.5 }}>
              Ready to assign SPOC for <span style={{ color: BLUE.main, fontWeight: 600 }}>{email}</span>
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: isDarkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>
              You will be <strong>logged out automatically</strong> after assignment.
            </Typography>
            {otpError && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: "8px", backgroundColor: isDarkMode ? "rgba(255,82,82,0.1)" : "#fff5f5", border: "1px solid rgba(244,67,54,0.2)" }}>
                <Typography sx={{ fontSize: "0.8rem", color: "#f44336" }}>{otpError}</Typography>
              </Box>
            )}
          </Box>
        )}

      </DialogContent>

      <Divider />

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>

        {/* SELF OTP STEP */}
        {step === STEP_SELF_OTP && (
          <>
            <Button onClick={handleClose} variant="outlined" size="small"
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 500, fontSize: "0.85rem", borderColor: isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)", color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)", "&:hover": { borderColor: BLUE.main, color: BLUE.main, backgroundColor: "transparent" } }}
            >Cancel</Button>

            {!selfOtpSent ? (
              <Button onClick={handleSendSelfOtp} variant="contained" size="small" disabled={loading}
                startIcon={!loading && <LockIcon sx={{ fontSize: "15px !important" }} />}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.85rem", background: BLUE.gradient, boxShadow: "0 3px 10px rgba(33,150,243,0.3)", "&:hover": { background: BLUE.gradientDark } }}
              >
                {loading ? <CircularProgress size={14} sx={{ color: "white" }} /> : "Send OTP to My Email"}
              </Button>
            ) : (
              <Button onClick={handleVerifySelfOtp} variant="contained" size="small" disabled={loading || selfOtp.length < 6}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.85rem", background: BLUE.gradient, boxShadow: "0 3px 10px rgba(33,150,243,0.3)", "&:hover": { background: BLUE.gradientDark }, "&.Mui-disabled": { opacity: 0.5 } }}
              >
                {loading ? <CircularProgress size={14} sx={{ color: "white" }} /> : "Verify & Continue"}
              </Button>
            )}
          </>
        )}

        {/* SEARCH STEP */}
        {step === STEP_SEARCH && (
          <>
            <Button onClick={handleClose} variant="outlined" size="small"
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 500, fontSize: "0.85rem", borderColor: isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)", color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)", "&:hover": { borderColor: BLUE.main, color: BLUE.main, backgroundColor: "transparent" } }}
            >Cancel</Button>
            {searchStatus === "found" && (
              <Button onClick={handleSendOtpDirect} variant="contained" size="small" disabled={loading}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.85rem", background: BLUE.gradient, boxShadow: "0 3px 10px rgba(33,150,243,0.3)", "&:hover": { background: BLUE.gradientDark } }}
              >
                {loading && <CircularProgress size={14} sx={{ color: "white", mr: 1 }} />}
                Send OTP
              </Button>
            )}
          </>
        )}

        {/* REGISTER STEP */}
        {step === STEP_REGISTER && (
          <>
            <Button onClick={() => setStep(STEP_SEARCH)} variant="outlined" size="small"
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 500, fontSize: "0.85rem", borderColor: isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)", color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)", "&:hover": { borderColor: BLUE.main, color: BLUE.main, backgroundColor: "transparent" } }}
            >Back</Button>
            <Button onClick={handleSendOtp} variant="contained" size="small" disabled={loading}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.85rem", background: BLUE.gradient, boxShadow: "0 3px 10px rgba(33,150,243,0.3)", "&:hover": { background: BLUE.gradientDark } }}
            >
              {loading && <CircularProgress size={14} sx={{ color: "white", mr: 1 }} />}
              Send OTP
            </Button>
          </>
        )}

        {/* OTP STEP */}
        {step === STEP_OTP && (
          <>
            <Button onClick={handleOtpBack} variant="outlined" size="small"
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 500, fontSize: "0.85rem", borderColor: isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)", color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)", "&:hover": { borderColor: BLUE.main, color: BLUE.main, backgroundColor: "transparent" } }}
            >Back</Button>
            <Button onClick={handleVerifyOtp} variant="contained" size="small" disabled={loading || otp.length < 6}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.85rem", background: BLUE.gradient, boxShadow: "0 3px 10px rgba(33,150,243,0.3)", "&:hover": { background: BLUE.gradientDark }, "&.Mui-disabled": { opacity: 0.5 } }}
            >
              {loading && <CircularProgress size={14} sx={{ color: "white", mr: 1 }} />}
              Verify OTP
            </Button>
          </>
        )}

        {/* DONE STEP */}
        {step === STEP_DONE && (
          <Button onClick={handleAssignSpoc} fullWidth variant="contained" size="small" disabled={loading}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.85rem", background: BLUE.gradient, boxShadow: "0 3px 10px rgba(33,150,243,0.3)", "&:hover": { background: BLUE.gradientDark } }}
          >
            {loading && <CircularProgress size={14} sx={{ color: "white", mr: 1 }} />}
            Assign SPOC & Logout
          </Button>
        )}

      </DialogActions>
    </Dialog>
  );
};

export default AssignSpocDialog;
